import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { useLoadingStore } from "./loading/loading.store";

const API_BASE_URL = import.meta.env.VITE_APP_API_URL as string | undefined;
const API_KEY = import.meta.env.VITE_APP_API_KEY as string | undefined;

const REQUEST_TIMEOUT_MS = 180_000;
const TOKEN_KEY = "token";
const EXPIRATION_KEY = "expiration";

function loadingStore() {
  return useLoadingStore();
}

let safety: number | null = null;

function armSafety() {
  if (safety !== null) return;
  safety = window.setTimeout(() => {
    console.warn("Loading safety reset triggered");
    loadingStore().reset();
    safety = null;
  }, 30_000);
}

function disarmSafetyIfDone() {
  if (loadingStore().state.count === 0 && safety !== null) {
    clearTimeout(safety);
    safety = null;
  }
}

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function isTokenExpired(): boolean {
  try {
    const iso = localStorage.getItem(EXPIRATION_KEY);
    if (!iso) return true;

    const expMs = Date.parse(iso);
    if (!Number.isFinite(expMs)) return true;

    return expMs <= Date.now();
  } catch {
    return true;
  }
}

function clearStoredAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRATION_KEY);
  } catch {
    // ignore
  }
}

function isPublicRoute() {
  const path = window.location.pathname.toLowerCase();
  return path.startsWith("/login");
}

function isAuthTokenRequest(config: AxiosRequestConfig): boolean {
  const url = config.url ?? "";
  return url.includes("/auth/token");
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

if (!API_BASE_URL) {
  console.warn("VITE_APP_API_URL is not set; API requests will fail");
}

let seq = 0;

api.interceptors.request.use(
  (config) => {
    const id = ++seq;
    (config as any).__id = id;

    const isPublic = isAuthTokenRequest(config) || isPublicRoute();
    (config as any).__showLoading = !isPublic;

    // Only show global spinner for non-public routes
    if (!isPublic) {
      loadingStore().show("Loading...", true);
      armSafety();
    }

    // Headers
    config.headers = config.headers ?? {};

    if (isAuthTokenRequest(config) && API_KEY) {
      config.headers["X-API-KEY"] = API_KEY;
    } else {
      const token = getStoredToken();
      if (token && !isTokenExpired()) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Debug
    console.log("[SHOW]", id, config.method?.toUpperCase(), config.baseURL, config.url);

    return config;
  },
  (error) => {
    // request setup failed
    loadingStore().hide();
    disarmSafetyIfDone();
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (res) => {
    console.log("[HIDE]", (res.config as any).__id, res.status, res.config.url);

    if ((res.config as any).__showLoading) {
      loadingStore().hide();
      disarmSafetyIfDone();
    }

    return res;
  },
  (error) => {
    const cfg = error?.config as any;
    console.log("[HIDE:ERR]", cfg?.__id ?? "no-config", error?.response?.status, cfg?.url);

    if (cfg?.__showLoading) {
      loadingStore().hide();
      disarmSafetyIfDone();
    }

    // auth failure handling
    if (error?.response?.status === 401) {
      clearStoredAuth();
      loadingStore().reset();
    }

    return Promise.reject(error);
  }
);

export default api;
